import { Dispatch, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getNKOForeignAgentData } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';

import { knmInfoGetNKOForeignAgentDataByOgrn, knmInfoSetNKOForeignAgentDataByOgrn } from '../actions/knmActions';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { INKOForeignAgent } from '../../../APIPackage/CatalogsModule/interfaces/INKOForeignAgent';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { formatDateLocale } from '../../../CorePackage/MainModule/common/formatDate';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import {
  DATE_FORMAT_LONG_FNS,
  SYSTEM_DATE_FORMAT_SHORT_FNS,
} from '../../../CorePackage/MainModule/constants/dateFormats';
import { KnmNkoField } from './KnmNkoField';

interface IKnmSubjectNkoInfoProps {
  checkByInnMessage: string | undefined;
  ervkId: string | undefined;
  formRef: IKnmFormRef | undefined;
  id: string | undefined;
  inspectionResult?: IInspectionResult;
  isShowMessages?: boolean;
  name: string | undefined;
  ogrn: string | undefined;
}

interface IKnmSubjectNkoInfoStoreProps {
  nkoForeignAgentData: INKOForeignAgent | undefined;
}

const KnmSubjectNkoInfoWithoutStore = ({
  checkByInnMessage,
  ervkId,
  formRef,
  id,
  inspectionResult,
  isShowMessages,
  name,
  ogrn,
  nkoForeignAgentData,
}: IKnmSubjectNkoInfoProps & IKnmSubjectNkoInfoStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [nkoContentLabel, setNkoContentLabel] = useState('');

  useEffect(() => {
    if (ogrn) {
      if (ogrn[0] !== '0' && (/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn))) {
        dispatch(knmInfoGetNKOForeignAgentDataByOgrn(ogrn));
        setNkoContentLabel('Нет');
        formRef?.current.change(`${name}.isNko` as keyof IKnmForm, false);
      } else {
        setNkoContentLabel('');
        dispatch(knmInfoSetNKOForeignAgentDataByOgrn(undefined));
        formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
      }
    } else {
      setNkoContentLabel('');
      dispatch(knmInfoSetNKOForeignAgentDataByOgrn(undefined));
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
    }
  }, [dispatch, ogrn, name, formRef]);

  useEffect(() => {
    if (nkoForeignAgentData && ogrn) {
      setNkoContentLabel('Да');
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm, true);
      formRef?.current.change(
        `${name}.nkoForeignAgentsRegistryNumber` as keyof IKnmForm,
        nkoForeignAgentData.registryNumber,
      );
      formRef?.current.change(
        `${name}.nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm,
        formatDateLocale({
          date: nkoForeignAgentData.inclusionDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
          parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
        }),
      );
      if (nkoForeignAgentData.exclusionDate) {
        formRef?.current.change(
          `${name}.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm,
          formatDateLocale({
            date: nkoForeignAgentData.exclusionDate,
            dateFormat: DATE_FORMAT_LONG_FNS,
            parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
          }),
        );
      } else {
        formRef?.current.change(`${name}.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm, undefined);
      }
    } else if (ogrn && (ogrn[0] === '0' || !(/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn)))) {
      setNkoContentLabel('');
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
    }
  }, [nkoForeignAgentData, formRef, name, ogrn]);

  const warning = () => {
    if (ervkId) {
      return undefined;
    }

    if (isShowMessages && checkByInnMessage) {
      return 'Сохраните КНМ и ожидайте загрузки информации из реестра НКО, время ожидания составляет до 2 часов';
    }

    return undefined;
  };

  return (
    <>
      {/* Блок информации о наличии контролируемого лица в реестре НКО-иноагентов */}
      <Row>
        <KnmNkoField
          id={`${id}.isNko`}
          value={nkoContentLabel}
          inspectionResult={inspectionResult}
          name={`${name}.isNko`}
          warningMessage={warning()}
          formRef={formRef}
        />
      </Row>

      {nkoForeignAgentData && (
        <Row>
          <FieldBlock label="Регистрационный номер" placementLabel="left" size="half" labelSize="col-3">
            <TextInputField
              id={`${id}.nkoForeignAgentsRegistryNumber`}
              className="shared-row-field"
              name={`${name}.nkoForeignAgentsRegistryNumber`}
              disabled
            />
          </FieldBlock>
          <FieldBlock size="half">
            <Row>
              <FieldBlock label="Дата включения" placementLabel="left" size="half">
                <TextInputField
                  id={`${id}.nkoForeignAgentsRegistryInclusionDate`}
                  className="shared-row-field"
                  name={`${name}.nkoForeignAgentsRegistryInclusionDate`}
                  disabled
                />
              </FieldBlock>

              {nkoForeignAgentData?.exclusionDate && (
                <FieldBlock label="Дата исключения" placementLabel="left" size="half">
                  <TextInputField
                    id={`${id}.nkoForeignAgentsRegistryExclusionDate`}
                    className="shared-row-field"
                    name={`${name}.nkoForeignAgentsRegistryExclusionDate`}
                    disabled
                  />
                </FieldBlock>
              )}
            </Row>
          </FieldBlock>
        </Row>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmSubjectNkoInfoStoreProps => ({
  nkoForeignAgentData: getNKOForeignAgentData(state),
});

export const KnmSubjectNkoInfo = connect(mapStateToProps)(KnmSubjectNkoInfoWithoutStore);
